<template>
  <Teleport to="body">
    <transition enter-active-class="transition duration-200 ease-out" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition duration-150 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
      <div v-if="open" class="fixed inset-0 z-[1100] flex flex-col bg-slate-100" role="dialog" aria-modal="true" aria-label="Broadcast editor">
        <header class="flex min-h-[72px] flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
          <button type="button" class="editor-icon-button" aria-label="Back to broadcasts" @click="emit('close')">
            <ArrowLeftIcon class="h-5 w-5" />
          </button>
          <div class="min-w-[220px] flex-1 sm:max-w-xl">
            <label class="sr-only" for="broadcast-name">Internal broadcast name</label>
            <input id="broadcast-name" v-model="draft.name" class="editor-name-input" placeholder="Internal broadcast name" type="text" />
            <p class="mt-1 text-[11px] text-slate-400">Only you and your team see this name.</p>
          </div>
          <span class="hidden rounded-full px-3 py-1 text-xs font-semibold lg:inline-flex" :class="draft.channel === 'sms' && !selectedSmsProvider?.connected ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'">{{ draft.channel === 'sms' ? (selectedSmsProvider?.connected ? `${selectedSmsProvider.label} connected` : 'SMS provider required') : 'In-app delivery connected' }}</span>
          <button type="button" class="min-w-[108px] rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50" :disabled="saving || !toastCopyIsCustomized" :title="!toastCopyIsCustomized ? 'Customize the toast headline and message first' : ''" @click="submit('draft')">{{ saving ? 'Saving…' : 'Save draft' }}</button>
          <button type="button" class="inline-flex min-w-[126px] items-center justify-center gap-2 rounded-xl bg-[#2D8CFF] px-4 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50" :disabled="saving || (draft.channel === 'in-app' && !toastIsReadyToSend) || (draft.channel === 'sms' && !smsIsReadyToSend)" :title="toastSendRequirement" @click="submit(draft.delivery === 'schedule' ? 'schedule' : 'send')">
            <PaperAirplaneIcon class="h-4 w-4" />
            {{ saving ? 'Working…' : draft.delivery === 'schedule' ? 'Schedule' : 'Send now' }}
          </button>
          <div class="relative hidden h-10 w-36 shrink-0 items-center sm:flex" aria-live="polite" aria-atomic="true">
            <transition enter-active-class="transition-opacity duration-300 ease-out" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition-opacity duration-500 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
              <span v-if="saveSuccess && !saving" class="absolute inset-y-0 left-0 inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-bold text-emerald-600"><CheckCircleIcon class="h-5 w-5" />{{ saveSuccess }}</span>
            </transition>
          </div>
        </header>

        <div class="grid min-h-0 flex-1 lg:grid-cols-[250px_minmax(480px,1fr)_330px]">
          <aside class="hidden overflow-y-auto border-r border-slate-200 bg-white p-5 lg:block">
            <p class="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Message channel</p>
            <div class="mt-3 grid gap-2">
              <button v-for="channel in channels" :key="channel.id" type="button" class="flex items-center gap-3 rounded-xl border px-3 py-3 text-left text-sm font-semibold transition" :class="draft.channel === channel.id ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'" @click="draft.channel = channel.id">
                <component :is="channel.icon" class="h-5 w-5" />
                {{ channel.label }}
              </button>
            </div>

            <template v-if="draft.channel === 'in-app'">
              <div class="my-6 border-t border-slate-100" />
              <p class="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Add content</p>
              <p class="mt-2 text-xs leading-5 text-slate-500">Build your announcement with flexible content blocks.</p>
              <div class="mt-4 grid grid-cols-2 gap-2">
                <button v-for="block in blockPalette" :key="block.type" type="button" class="flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 px-2 py-4 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600" @click="addBlock(block.type)">
                  <component :is="block.icon" class="h-5 w-5" />
                  {{ block.label }}
                </button>
              </div>
            </template>

            <div class="mt-6 rounded-xl bg-slate-50 p-4">
              <div class="flex gap-2 text-xs leading-5 text-slate-500">
                <InformationCircleIcon class="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                <p>{{ draft.channel === 'sms' ? 'SMS text messages support spacing, line breaks, and links. Rich media blocks are available for in-app broadcasts.' : 'Select a block in the preview to edit its content and order.' }}</p>
              </div>
            </div>
          </aside>

          <main class="editor-canvas overflow-y-auto p-4 sm:p-7 lg:p-10">
            <div class="mx-auto max-w-[720px]">
              <div class="mb-4 flex items-center justify-between gap-3">
                <div>
                  <p class="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Live preview</p>
                  <p class="mt-1 text-sm text-slate-500">{{ draft.channel === 'sms' ? 'Member text message' : 'Member in-app announcement' }}</p>
                </div>
                <div class="flex rounded-xl bg-white p-1 shadow-sm ring-1 ring-slate-200">
                  <button type="button" class="rounded-lg px-3 py-2 text-xs font-bold" :class="previewMode === 'desktop' ? 'bg-slate-900 text-white' : 'text-slate-500'" @click="previewMode = 'desktop'">Desktop</button>
                  <button type="button" class="rounded-lg px-3 py-2 text-xs font-bold" :class="previewMode === 'mobile' ? 'bg-slate-900 text-white' : 'text-slate-500'" @click="previewMode = 'mobile'">Mobile</button>
                </div>
              </div>

              <div v-if="draft.channel === 'in-app'" class="relative mb-5 ml-auto w-full max-w-sm lg:translate-x-14 xl:translate-x-20">
                <div class="group absolute -left-10 top-2 z-10">
                  <button type="button" class="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm ring-1 ring-slate-200 hover:text-blue-600" aria-label="About this sample">
                    <InformationCircleIcon class="h-5 w-5" />
                  </button>
                  <span class="pointer-events-none absolute right-full top-0 mr-2 w-48 rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-semibold leading-4 text-white opacity-0 shadow-xl transition group-hover:opacity-100 group-focus-within:opacity-100">Editor sample only. Members receive the toast without editor labels or controls.</span>
                </div>
                <div class="rounded-2xl bg-zinc-700 px-4 py-4 text-white shadow-xl">
                  <div class="flex items-start gap-3">
                    <img v-if="broadcastAvatarSrc" :src="broadcastAvatarSrc" alt="Broadcast identity" class="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-white/30" />
                    <span v-else class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/10 text-sm font-bold">P</span>
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-sm font-bold">{{ draft.toastTitle || 'New announcement' }}</p>
                      <p class="mt-1 line-clamp-2 text-sm text-zinc-200">{{ draft.toastBody || 'Something new is waiting for you' }}</p>
                      <p class="mt-2 text-xs font-semibold text-blue-300">View announcement</p>
                    </div>
                    <XMarkIcon class="h-4 w-4 shrink-0 text-zinc-300" />
                  </div>
                </div>
              </div>
              <div class="mx-auto transition-all" :class="previewMode === 'mobile' ? 'max-w-[360px]' : previewWidthClass">
                <div v-if="draft.channel === 'in-app'" class="w-full bg-white shadow-2xl ring-1 ring-zinc-200" :style="previewSurfaceStyle">
                  <div class="relative p-8 text-center">
                    <span class="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-zinc-400"><XMarkIcon class="h-5 w-5" /></span>
                    <div class="mx-auto w-full max-w-[390px] text-left" :style="{ maxWidth: previewContentWidth }">
                    <div class="space-y-3">
                    <div v-for="block in draft.blocks" :key="block.id" class="group relative block w-full cursor-pointer rounded-xl border-2 p-1 text-left transition" :class="selectedBlockId === block.id ? 'border-blue-400 bg-blue-50/30' : 'border-transparent hover:border-blue-200'" role="button" tabindex="0" @click="selectedBlockId = block.id" @keydown.enter.prevent="selectedBlockId = block.id" @keydown.space.prevent="selectedBlockId = block.id">
                      <h2 v-if="block.type === 'heading'" class="font-bold leading-tight" :style="blockTextStyle(block)">{{ block.content || 'Your announcement headline' }}</h2>
                      <p v-else-if="block.type === 'text'" class="whitespace-pre-wrap leading-6" :style="blockTextStyle(block)">{{ block.content || 'Add helpful details for your members.' }}</p>
                      <div v-else-if="block.type === 'image'" class="overflow-hidden bg-slate-100" :style="{ borderRadius: `${block.radius}px` }">
                        <img v-if="broadcastImageSrc(block)" :src="broadcastImageSrc(block)" :alt="block.alt || 'Broadcast image'" class="max-h-[360px] w-full" :style="{ objectFit: block.imageFit || 'cover' }" />
                        <div v-else class="flex h-44 flex-col items-center justify-center text-slate-400"><PhotoIcon class="h-8 w-8" /><span class="mt-2 text-xs font-semibold">Choose an image from Media Content</span></div>
                      </div>
                      <div v-else-if="block.type === 'video'" class="relative aspect-video overflow-hidden bg-slate-900 text-white" :style="{ borderRadius: `${block.radius}px` }">
                        <PageEditorVideoPlayer
                          v-if="broadcastVideoFileId(block) || broadcastVideoPlaylistSrc(block)"
                          :key="`broadcast-video-${block.id}-${broadcastVideoFileId(block)}-${block.posterMediaFileId || 'generated'}-${block.thumbnailRevision || 0}`"
                          :file-id="broadcastVideoFileId(block)"
                          :playlist-url="broadcastVideoPlaylistSrc(block)"
                          :poster-url="broadcastVideoPosterSrc(block)"
                          :source-key="`broadcast-video-${block.id}`"
                          :controls-color="draft.design.accentColor"
                          :border-radius="Number(block.radius || 0)"
                        />
                        <div v-else class="flex h-full min-h-48 flex-col items-center justify-center text-slate-300"><VideoCameraIcon class="h-9 w-9" /><span class="mt-2 text-xs font-semibold">Choose a video from Media Content</span></div>
                      </div>
                      <span v-else class="inline-flex px-5 py-3 font-bold" :style="buttonBlockStyle(block)">{{ block.content || 'Learn more' }}</span>
                      <span class="pointer-events-none absolute right-2 top-2 rounded-md bg-blue-600 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white opacity-0 shadow group-hover:opacity-100">Edit</span>
                    </div>
                    </div>
                    </div>
                  </div>
                </div>

                <div v-else class="rounded-[30px] border-[8px] border-slate-900 bg-white px-4 py-6 shadow-2xl shadow-slate-300/50">
                  <div class="mx-auto mb-5 h-1.5 w-16 rounded-full bg-slate-800" />
                  <div class="mb-5 text-center"><div class="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 font-bold text-slate-600">P</div><p class="mt-2 text-xs font-semibold text-slate-700">Your member platform</p></div>
                  <div class="max-w-[88%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-md bg-slate-100 px-4 py-3 text-sm leading-6 text-slate-700">{{ draft.smsMessage || 'Write your text message in the settings panel.' }}</div>
                  <p class="mt-2 text-right text-[10px] text-slate-400">{{ smsSegments }} {{ smsSegments === 1 ? 'segment' : 'segments' }}</p>
                  <div class="h-32" />
                </div>
              </div>
            </div>
          </main>

          <aside class="editor-settings overflow-y-auto border-l border-slate-200 bg-white p-5">
            <h2 class="font-bold text-slate-900">Broadcast settings</h2>
            <p class="mt-1 text-xs leading-5 text-slate-500">Configure the audience and delivery timing.</p>

            <p v-if="saveError || validationError" class="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">{{ validationError || saveError }}</p>

            <div v-if="draft.channel === 'in-app'" class="mt-5 rounded-xl border p-3 transition" :class="toastIsReadyToSend ? 'border-slate-200' : 'border-amber-300 bg-amber-50/70 ring-2 ring-amber-100'">
              <h3 class="text-xs font-bold text-slate-800">Toast message</h3>
              <p class="mt-1 text-[11px] leading-5 text-slate-500">Customize the compact message members see before opening the full broadcast.</p>
              <p v-if="!toastCopyIsCustomized" class="mt-3 rounded-lg bg-amber-100 px-3 py-2 text-[11px] font-semibold leading-5 text-amber-800">Customize both fields before saving this broadcast.</p>
              <p v-else-if="!toastAvatarIsSelected" class="mt-3 rounded-lg bg-amber-100 px-3 py-2 text-[11px] font-semibold leading-5 text-amber-800">Choose an engaging-as avatar before sending or scheduling. You can still save this draft.</p>
              <label class="mt-4 block"><span class="editor-label">Toast headline</span><input v-model="draft.toastTitle" class="editor-control" :class="!toastHeadlineIsCustomized ? '!border-amber-400' : ''" maxlength="255" placeholder="New announcement" type="text" /></label>
              <label class="mt-4 block"><span class="editor-label">Toast message</span><textarea v-model="draft.toastBody" class="editor-control min-h-[86px] resize-y" :class="!toastBodyIsCustomized ? '!border-amber-400' : ''" maxlength="1000" placeholder="Something new is waiting for you" /></label>
              <span class="editor-label mt-4">Engaging as</span>
              <div class="flex items-center gap-3 rounded-xl border p-2 transition" :class="toastAvatarIsSelected ? 'border-transparent' : 'border-amber-400 bg-white'">
                <img v-if="broadcastAvatarSrc" :src="broadcastAvatarSrc" alt="Selected broadcast identity" class="h-11 w-11 rounded-full object-cover ring-1 ring-slate-200" />
                <span v-else class="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-500">P</span>
                <button type="button" class="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs font-bold text-blue-700 hover:bg-blue-100" @click="openMediaPicker('avatar')"><PhotoIcon class="h-4 w-4" /> {{ draft.avatarMediaFileId ? 'Change image' : 'Choose image' }}</button>
              </div>
              <button v-if="draft.avatarMediaFileId" type="button" class="mt-2 w-full text-center text-xs font-bold text-slate-500 hover:text-blue-600" @click="draft.avatarMediaFileId = ''">Use platform logo</button>
            </div>

            <label class="mt-5 block">
              <span class="editor-label">Audience</span>
              <PlatformaSelect v-model="draft.audience" :options="audienceSelectOptions" />
            </label>

            <label v-if="draft.audience === 'tier'" class="mt-4 block">
              <span class="editor-label">Plan tier</span>
              <PlatformaSelect v-model="draft.audiencePlanTierId" :options="tierSelectOptions" placeholder="Select a plan tier" />
              <p v-if="!tiers.length" class="mt-2 text-xs text-amber-600">No active plan tiers are available.</p>
            </label>

            <div v-if="draft.audience === 'selected'" class="mt-4">
              <label class="block"><span class="editor-label">Find members</span><input v-model="memberSearch" class="editor-control" placeholder="Search name or email" type="search" /></label>
              <div class="mt-2 max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white">
                <label v-for="member in filteredMembers" :key="member.id" class="flex cursor-pointer items-center gap-3 border-b border-slate-100 px-3 py-2.5 last:border-0 hover:bg-slate-50">
                  <input v-model="draft.audienceMemberIds" :value="Number(member.id)" type="checkbox" class="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                  <img :src="member.avatarUrl || defaultMemberAvatar" :alt="member.name" class="h-8 w-8 rounded-full object-cover" @error="$event.target.src = defaultMemberAvatar" />
                  <span class="min-w-0"><span class="block truncate text-xs font-bold text-slate-700">{{ member.name }}</span><span class="block truncate text-[11px] text-slate-400">{{ member.email }}</span></span>
                </label>
                <p v-if="!filteredMembers.length" class="px-3 py-5 text-center text-xs text-slate-400">No matching active members.</p>
              </div>
              <p class="mt-2 text-xs font-semibold text-blue-600">{{ draft.audienceMemberIds.length }} selected</p>
            </div>

            <fieldset class="mt-5">
              <legend class="editor-label">Delivery</legend>
              <div class="grid grid-cols-2 rounded-xl bg-slate-100 p-1">
                <button v-for="option in deliveryOptions" :key="option.id" type="button" class="rounded-lg px-2 py-2.5 text-xs font-bold transition" :class="draft.delivery === option.id ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'" @click="draft.delivery = option.id">{{ option.label }}</button>
              </div>
            </fieldset>

            <div v-if="draft.delivery === 'schedule'" class="mt-5 rounded-xl border border-blue-100 bg-blue-50/60 p-3">
              <span class="editor-label">Schedule</span>
              <WebinarEventScheduler v-model:date="draft.date" v-model:time="draft.time" :timezone="draft.timezone" />
              <label class="mt-3 block">
                <span class="editor-label">Time zone</span>
                <PlatformaSelect v-model="draft.timezone" :options="timezoneOptions" />
              </label>
            </div>

            <div v-if="draft.channel === 'in-app'" class="mt-5 rounded-xl border border-slate-200 p-3">
              <button type="button" class="flex w-full items-start justify-between gap-3 text-left" @click="draft.deleteAfterDelivery = !draft.deleteAfterDelivery">
                <span><span class="block text-xs font-bold text-slate-800">Schedule notification removal</span><span class="mt-1 block text-[11px] leading-5 text-slate-500">Remove this announcement from members’ notification lists after delivery.</span></span>
                <span class="relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition" :class="draft.deleteAfterDelivery ? 'bg-blue-600' : 'bg-slate-200'"><span class="absolute top-1 h-4 w-4 rounded-full bg-white shadow transition" :class="draft.deleteAfterDelivery ? 'left-6' : 'left-1'" /></span>
              </button>
              <div v-if="draft.deleteAfterDelivery" class="mt-4 border-t border-slate-100 pt-4">
                <WebinarEventScheduler v-model:date="draft.deleteDate" v-model:time="draft.deleteTime" :timezone="draft.timezone" />
                <p class="mt-2 text-[11px] leading-5 text-slate-500">This must be later than the scheduled delivery time.</p>
              </div>
            </div>

            <template v-if="draft.channel === 'in-app'">
              <div class="my-6 border-t border-slate-100" />
              <h3 class="text-sm font-bold text-slate-900">Modal appearance</h3>
              <div class="mt-4 grid grid-cols-2 gap-3">
                <label class="block"><span class="editor-label">Accent</span><input v-model="draft.design.accentColor" type="color" class="editor-color" /></label>
                <label class="block"><span class="editor-label">Background</span><input v-model="draft.design.surfaceColor" type="color" class="editor-color" /></label>
              </div>
              <label class="mt-4 block"><span class="editor-label">Modal width</span><PlatformaSelect v-model="draft.design.width" :options="modalWidthOptions" /></label>
              <label class="mt-4 block"><span class="editor-label">Corner style</span><PlatformaSelect v-model="draft.design.radius" :options="radiusOptions" /></label>
            </template>

            <template v-if="draft.channel === 'sms'">
              <div class="mt-5 rounded-xl border p-3 text-xs leading-5" :class="selectedSmsProvider?.connected ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'">
                {{ selectedSmsProvider?.connected ? (draft.smsProviderMode === 'platforma_twilio' ? 'Platforma will show the complete price and ask for one confirmation before charging your saved creator card.' : 'Twilio will bill your connected account directly.') : 'Connect Twilio in Membership Settings → Integrations & API, or ask a Super Admin to enable Platforma SMS Text Messages.' }}
              </div>
              <label class="mt-5 block"><span class="editor-label">Send from</span><PlatformaSelect v-model="draft.smsProviderMode" :options="smsProviderOptions" /></label>
              <label class="mt-6 block"><span class="editor-label">SMS Text Message</span><textarea v-model="draft.smsMessage" class="editor-control min-h-[190px] resize-y whitespace-pre-wrap" maxlength="1600" placeholder="Write a concise text message..." /><span class="mt-1 block text-right text-[11px] text-slate-400">{{ draft.smsMessage.length }}/1600 · {{ smsSegments }} {{ smsSegments === 1 ? 'segment' : 'segments' }}</span></label>
              <p class="mt-3 text-[11px] leading-5 text-slate-500">Only members with a valid mobile number and active text-message preference are included.</p>
            </template>

            <template v-else-if="selectedBlock">
              <div class="my-6 border-t border-slate-100" />
              <div class="flex items-center justify-between"><h3 class="text-sm font-bold text-slate-900">Edit {{ blockLabel(selectedBlock.type) }}</h3><button type="button" class="text-red-500 hover:text-red-600" aria-label="Delete content block" @click="removeBlock(selectedBlock.id)"><TrashIcon class="h-5 w-5" /></button></div>
              <label v-if="['heading', 'text', 'button'].includes(selectedBlock.type)" class="mt-4 block"><span class="editor-label">{{ selectedBlock.type === 'button' ? 'Button label' : 'Content' }}</span><textarea v-model="selectedBlock.content" class="editor-control min-h-[100px] resize-y" /></label>
              <button v-if="selectedBlock.type === 'image'" type="button" class="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs font-bold text-blue-700 hover:bg-blue-100" @click="openMediaPicker('image')"><PhotoIcon class="h-4 w-4" /> Choose or upload image</button>
              <label v-if="selectedBlock.type === 'image'" class="mt-4 block"><span class="editor-label">Alt text</span><input v-model="selectedBlock.alt" class="editor-control" placeholder="Describe the image" type="text" /></label>
              <button v-if="selectedBlock.type === 'video'" type="button" class="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs font-bold text-blue-700 hover:bg-blue-100" @click="openMediaPicker('video')"><VideoCameraIcon class="h-4 w-4" /> Choose or upload video</button>
              <template v-if="selectedBlock.type === 'video'">
                <button type="button" class="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700" @click="openMediaPicker('video-poster')"><PhotoIcon class="h-4 w-4" /> {{ selectedBlock.posterMediaFileId ? 'Change thumbnail image' : 'Choose thumbnail image' }}</button>
                <button v-if="selectedBlock.posterMediaFileId" type="button" class="mt-2 w-full text-center text-xs font-bold text-slate-500 hover:text-blue-600" @click="clearVideoPoster">Use generated video thumbnail</button>
                <p class="mt-2 text-[11px] leading-5 text-slate-500">Select a thumbnail from Platforma Media, or use the thumbnail generated from the video.</p>
                <p v-if="selectedBlock.thumbnailPending" class="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-[11px] font-semibold leading-5 text-blue-700">Platforma is preparing this video and its thumbnail. The preview will refresh automatically.</p>
              </template>
              <template v-if="selectedBlock.type === 'button'">
                <label class="mt-4 block"><span class="editor-label">On click</span><PlatformaSelect v-model="selectedBlock.buttonActionType" :options="buttonActionTypeOptions" /></label>
                <label v-if="selectedBlock.buttonActionType === 'go_to'" class="mt-4 block"><span class="editor-label">Destination</span><PlatformaSelect v-model="selectedBlock.buttonGoToType" :options="buttonGoToTypeOptions" /></label>
                <label v-if="selectedBlock.buttonActionType === 'go_to' && selectedBlock.buttonGoToType === 'feature_instance'" class="mt-4 block"><span class="editor-label">Feature instance</span><PlatformaSelect v-model="selectedBlock.buttonFeatureSlug" :options="featureSelectOptions" placeholder="Select feature instance" /></label>
                <p v-if="selectedBlock.buttonActionType === 'go_to' && selectedBlock.buttonGoToType === 'feature_instance' && !featureSelectOptions.length" class="mt-2 text-xs leading-5 text-amber-600">Add a membership feature in the Theme Editor before linking this button.</p>
                <label v-if="selectedBlock.buttonActionType === 'go_to' && selectedBlock.buttonGoToType === 'custom_link'" class="mt-4 block"><span class="editor-label">Destination URL</span><input v-model="selectedBlock.url" class="editor-control" placeholder="/member-account/dashboard" type="text" /></label>
                <label v-if="selectedBlock.buttonActionType === 'go_to'" class="mt-4 flex cursor-pointer items-center gap-3 text-xs font-semibold text-slate-700"><input v-model="selectedBlock.buttonOpenInNewTab" type="checkbox" class="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" /> Open in new tab</label>
              </template>
              <label v-if="['heading', 'text', 'button'].includes(selectedBlock.type)" class="mt-4 block"><span class="editor-label">Alignment</span><PlatformaSelect v-model="selectedBlock.align" :options="selectedBlock.type === 'button' ? buttonAlignmentOptions : alignmentOptions" /></label>
              <label v-if="['heading', 'text'].includes(selectedBlock.type)" class="mt-4 block"><span class="editor-label">Text size</span><PlatformaSelect v-model="selectedBlock.fontSize" :options="selectedBlock.type === 'heading' ? headingSizeOptions : textSizeOptions" /></label>
              <label v-if="['heading', 'text'].includes(selectedBlock.type)" class="mt-4 block"><span class="editor-label">Text color</span><input v-model="selectedBlock.color" type="color" class="editor-color" /></label>
              <template v-if="selectedBlock.type === 'button'"><div class="mt-4 grid grid-cols-2 gap-3"><label><span class="editor-label">Button</span><input v-model="selectedBlock.backgroundColor" type="color" class="editor-color" /></label><label><span class="editor-label">Text</span><input v-model="selectedBlock.buttonTextColor" type="color" class="editor-color" /></label></div></template>
              <label v-if="['image', 'video', 'button'].includes(selectedBlock.type)" class="mt-4 block"><span class="editor-label">Corner style</span><PlatformaSelect v-model="selectedBlock.radius" :options="radiusOptions" /></label>
              <label v-if="selectedBlock.type === 'image'" class="mt-4 block"><span class="editor-label">Image fit</span><PlatformaSelect v-model="selectedBlock.imageFit" :options="imageFitOptions" /></label>
              <div class="mt-4 grid grid-cols-2 gap-2">
                <button type="button" class="editor-move-button" :disabled="selectedIndex === 0" @click="moveBlock(-1)"><ChevronUpIcon class="h-4 w-4" /> Move up</button>
                <button type="button" class="editor-move-button" :disabled="selectedIndex === draft.blocks.length - 1" @click="moveBlock(1)"><ChevronDownIcon class="h-4 w-4" /> Move down</button>
              </div>
            </template>
          </aside>
        </div>
      </div>
    </transition>
  </Teleport>

  <WebinarMediaPickerModal
    :show="Boolean(activeMediaPicker)"
    :media-type="activeMediaPicker === 'video' ? 'video' : 'image'"
    :title="activeMediaPicker === 'video' ? 'Choose broadcast video' : activeMediaPicker === 'video-poster' ? 'Choose video thumbnail' : activeMediaPicker === 'avatar' ? 'Choose engaging-as image' : 'Choose broadcast image'"
    upload-origin="creator-broadcast-media"
    z-index-class="z-[2100]"
    @close="activeMediaPicker = ''"
    @select="applySelectedMedia"
  />
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { ArrowLeftIcon, BellAlertIcon, ChatBubbleLeftRightIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, CursorArrowRaysIcon, DocumentTextIcon, InformationCircleIcon, PaperAirplaneIcon, PhotoIcon, TrashIcon, VideoCameraIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import PlatformaSelect from '@/Components/PlatformaSelect.vue'
import PageEditorVideoPlayer from '@/Components/PageEditorVideoPlayer.vue'
import WebinarEventScheduler from '@/Components/WebinarEventScheduler.vue'
import WebinarMediaPickerModal from '@/Components/WebinarMediaPickerModal.vue'
import defaultMemberAvatar from '@/assets/media/images/default-avatar.png'

const props = defineProps({
  open: { type: Boolean, default: false },
  broadcast: { type: Object, default: null },
  members: { type: Array, default: () => [] },
  tiers: { type: Array, default: () => [] },
  featureInstances: { type: Array, default: () => [] },
  smsProviders: { type: Array, default: () => [] },
  saving: { type: Boolean, default: false },
  saveError: { type: String, default: '' },
  saveSuccess: { type: String, default: '' },
})
const emit = defineEmits(['close', 'save', 'autosave', 'search-members'])
const DEFAULT_TOAST_TITLE = 'New announcement'
const DEFAULT_TOAST_BODY = 'Something new is waiting for you'
const channels = [{ id: 'in-app', label: 'In-App Notification', icon: BellAlertIcon }, { id: 'sms', label: 'SMS Text Message', icon: ChatBubbleLeftRightIcon }]
const blockPalette = [{ type: 'heading', label: 'Heading', icon: DocumentTextIcon }, { type: 'text', label: 'Text', icon: DocumentTextIcon }, { type: 'image', label: 'Image', icon: PhotoIcon }, { type: 'video', label: 'Video', icon: VideoCameraIcon }, { type: 'button', label: 'Button', icon: CursorArrowRaysIcon }]
const deliveryOptions = [{ id: 'now', label: 'Send now' }, { id: 'schedule', label: 'Schedule' }]
const timezoneOptions = [
  { value: 'America/New_York', label: 'Eastern Time' }, { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' }, { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'UTC', label: 'UTC' },
]
const modalWidthOptions = [{ value: 'compact', label: 'Compact' }, { value: 'standard', label: 'Standard' }, { value: 'wide', label: 'Wide' }]
const radiusOptions = [{ value: 0, label: 'Square' }, { value: 8, label: 'Soft' }, { value: 12, label: 'Rounded' }, { value: 20, label: 'Extra rounded' }]
const alignmentOptions = [{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]
const buttonAlignmentOptions = [{ value: 'stretch', label: 'Full width' }, ...alignmentOptions]
const headingSizeOptions = [{ value: 20, label: 'Small' }, { value: 24, label: 'Medium' }, { value: 30, label: 'Large' }, { value: 36, label: 'Extra large' }]
const textSizeOptions = [{ value: 12, label: 'Small' }, { value: 14, label: 'Medium' }, { value: 16, label: 'Large' }, { value: 18, label: 'Extra large' }]
const imageFitOptions = [{ value: 'cover', label: 'Fill frame' }, { value: 'contain', label: 'Show full image' }]
const buttonActionTypeOptions = [{ value: 'go_to', label: 'Go To...' }]
const buttonGoToTypeOptions = [{ value: 'custom_link', label: 'Custom Action Link' }, { value: 'feature_instance', label: 'Membership Feature Instance' }]
const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/+$/, '')
const previewMode = ref('desktop')
const selectedBlockId = ref(null)
const memberSearch = ref('')
const validationError = ref('')
const activeMediaPicker = ref('')
let blockSequence = 10
let memberSearchTimer = null
let autosaveTimer = null
let hydratingDraft = false
let lastAutosaveSignature = ''
const thumbnailRefreshTimers = new Map()
const freshDraft = () => ({
  id: null, slug: '', name: 'New member announcement', channel: 'in-app', audience: 'all', audiencePlanTierId: '', audienceMemberIds: [],
  toastTitle: DEFAULT_TOAST_TITLE, toastBody: DEFAULT_TOAST_BODY, avatarMediaFileId: '',
  delivery: 'now', date: '', time: '', timezone: 'America/New_York', deleteAfterDelivery: false, deleteDate: '', deleteTime: '', smsMessage: '', smsProviderMode: 'creator_twilio', smsQuoteId: '',
  design: { accentColor: '#2D8CFF', surfaceColor: '#FFFFFF', width: 'standard', radius: 12 },
  blocks: [
    { id: 1, type: 'heading', content: 'Something new is waiting for you', align: 'left', color: '#18181B', fontSize: 24 },
    { id: 2, type: 'text', content: 'We just added something special to your member experience. Open your account to take a look.', align: 'left', color: '#52525B', fontSize: 14 },
    { id: 3, type: 'button', content: 'Open my account', url: '/member-account', align: 'stretch', backgroundColor: '#2D8CFF', buttonTextColor: '#FFFFFF', radius: 8, buttonActionType: 'go_to', buttonGoToType: 'custom_link', buttonFeatureSlug: '', buttonOpenInNewTab: false },
  ],
})
const draft = reactive(freshDraft())

watch(() => props.open, (open) => {
  if (!open) {
    if (autosaveTimer) clearTimeout(autosaveTimer)
    clearThumbnailRefreshTimers()
    return
  }
  hydratingDraft = true
  const source = props.broadcast ? { ...freshDraft(), ...props.broadcast } : freshDraft()
  source.blocks = Array.isArray(props.broadcast?.blocks) ? props.broadcast.blocks.map(normalizeEditorBlock) : freshDraft().blocks
  source.design = { ...freshDraft().design, ...(props.broadcast?.design || {}) }
  source.audiencePlanTierId = source.audiencePlanTierId ? String(source.audiencePlanTierId) : ''
  source.audienceMemberIds = Array.isArray(source.audienceMemberIds) ? source.audienceMemberIds.map(Number).filter(Boolean) : []
  if (source.scheduledAt) {
    const scheduled = new Date(source.scheduledAt)
    if (!Number.isNaN(scheduled.getTime())) {
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: source.timezone || 'America/New_York', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(scheduled).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]))
      source.date = `${parts.year}-${parts.month}-${parts.day}`
      source.time = `${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}`
      source.delivery = 'schedule'
    }
  }
  if (source.deleteScheduledAt) {
    const removal = localParts(source.deleteScheduledAt, source.timezone)
    if (removal) {
      source.deleteAfterDelivery = true
      source.deleteDate = removal.date
      source.deleteTime = removal.time
    }
  }
  Object.assign(draft, source)
  selectedBlockId.value = draft.blocks[0]?.id || null
  previewMode.value = 'desktop'
  memberSearch.value = ''
  validationError.value = ''
  activeMediaPicker.value = ''
  lastAutosaveSignature = JSON.stringify(buildPayload('draft', true))
  setTimeout(() => { hydratingDraft = false }, 0)
  setTimeout(() => {
    draft.blocks.filter((block) => block.type === 'video' && block.mediaFileId && !block.posterMediaFileId)
      .forEach((block) => refreshGeneratedVideoThumbnail(block))
  }, 0)
})

watch(draft, () => {
  if (!props.open || hydratingDraft || props.saving) return
  if (autosaveTimer) clearTimeout(autosaveTimer)
  autosaveTimer = setTimeout(() => {
    if (!props.open || hydratingDraft || props.saving || !String(draft.name || '').trim()) return
    const payload = buildPayload('draft', true)
    const signature = JSON.stringify(payload)
    if (signature === lastAutosaveSignature) return
    lastAutosaveSignature = signature
    emit('autosave', payload)
  }, 1100)
}, { deep: true })

watch(() => props.broadcast, (broadcast) => {
  if (!props.open || !broadcast) return
  hydratingDraft = true
  if (broadcast.id != null) draft.id = broadcast.id
  if (broadcast.slug) draft.slug = String(broadcast.slug)
  lastAutosaveSignature = JSON.stringify(buildPayload('draft', true))
  setTimeout(() => { hydratingDraft = false }, 0)
})

watch(memberSearch, (query) => {
  if (memberSearchTimer) clearTimeout(memberSearchTimer)
  memberSearchTimer = setTimeout(() => emit('search-members', query.trim()), 300)
})

const selectedIndex = computed(() => draft.blocks.findIndex((block) => block.id === selectedBlockId.value))
const selectedBlock = computed(() => draft.blocks[selectedIndex.value] || null)
const toastHeadlineIsCustomized = computed(() => {
  const value = String(draft.toastTitle || '').trim()
  return Boolean(value) && value !== DEFAULT_TOAST_TITLE
})
const toastBodyIsCustomized = computed(() => {
  const value = String(draft.toastBody || '').trim()
  return Boolean(value) && value !== DEFAULT_TOAST_BODY
})
const toastCopyIsCustomized = computed(() => draft.channel === 'sms' || (toastHeadlineIsCustomized.value && toastBodyIsCustomized.value))
const toastAvatarIsSelected = computed(() => draft.channel === 'sms' || Boolean(String(draft.avatarMediaFileId || '').trim()))
const toastIsReadyToSend = computed(() => toastCopyIsCustomized.value && toastAvatarIsSelected.value)
const toastSendRequirement = computed(() => {
  if (draft.channel === 'sms') return ''
  if (!toastCopyIsCustomized.value) return 'Customize the toast headline and message first'
  if (!toastAvatarIsSelected.value) return 'Choose an engaging-as avatar first'
  return ''
})
const smsSegments = computed(() => Math.max(1, Math.ceil(draft.smsMessage.length / 160)))
const selectedSmsProvider = computed(() => props.smsProviders.find((provider) => provider.id === draft.smsProviderMode) || null)
const smsProviderOptions = computed(() => props.smsProviders.map((provider) => ({
  value: provider.id,
  label: `${provider.label}${provider.connected ? '' : ' — not connected'}`,
  disabled: !provider.connected,
})))
const smsIsReadyToSend = computed(() => Boolean(draft.smsMessage.trim() && selectedSmsProvider.value?.connected))
const filteredMembers = computed(() => {
  const query = memberSearch.value.trim().toLowerCase()
  if (!query) return props.members
  return props.members.filter((member) => `${member.name || ''} ${member.email || ''}`.toLowerCase().includes(query))
})
const audienceSelectOptions = computed(() => [
  { value: 'all', label: draft.channel === 'sms' ? 'All opted-in members' : 'All active members' },
  { value: 'tier', label: 'Members in a plan tier' },
  { value: 'selected', label: 'Selected members' },
])
const tierSelectOptions = computed(() => props.tiers.map((tier) => ({ value: String(tier.id), label: tier.name })))
const featureSelectOptions = computed(() => props.featureInstances.map((feature) => ({
  value: String(feature.slug || '').trim(),
  label: String(feature.name || feature.customName || feature.featureName || feature.slug || 'Membership feature').trim(),
})).filter((option) => option.value))
const previewWidthClass = computed(() => ({ compact: 'max-w-[360px]', standard: 'max-w-md', wide: 'max-w-[560px]' }[draft.design.width] || 'max-w-md'))
const previewContentWidth = computed(() => ({ compact: '296px', standard: '390px', wide: '500px' }[draft.design.width] || '390px'))
const previewSurfaceStyle = computed(() => ({ backgroundColor: draft.design.surfaceColor, borderRadius: `${Number(draft.design.radius || 0)}px` }))
const broadcastAvatarSrc = computed(() => {
  const fileId = String(draft.avatarMediaFileId || '').trim()
  return fileId ? `${apiProxyUrl}/api/image/${encodeURIComponent(fileId)}` : ''
})
const blockLabel = (type) => ({ heading: 'heading', text: 'text', image: 'image', video: 'video', button: 'button' }[type] || 'block')

function normalizeEditorBlock(block = {}) {
  const type = ['heading', 'text', 'image', 'video', 'button'].includes(block.type) ? block.type : 'text'
  const defaults = {
    heading: { align: 'left', color: '#18181B', fontSize: 24 },
    text: { align: 'left', color: '#52525B', fontSize: 14 },
    image: { align: 'left', radius: 12, imageFit: 'cover', mediaFileId: '' },
    video: { align: 'left', radius: 12, mediaFileId: '', posterMediaFileId: '', thumbnailPending: false, thumbnailRevision: 0 },
    button: { align: 'stretch', backgroundColor: '#2D8CFF', buttonTextColor: '#FFFFFF', fontSize: 14, radius: 8, buttonActionType: 'go_to', buttonGoToType: 'custom_link', buttonFeatureSlug: '', buttonOpenInNewTab: false },
  }
  return { ...defaults[type], ...block, type }
}

function addBlock(type) {
  const presets = {
    heading: { content: 'Your announcement headline', align: 'left', color: '#18181B', fontSize: 24 },
    text: { content: 'Add helpful details for your members.', align: 'left', color: '#52525B', fontSize: 14 },
    image: { url: '', alt: '', mediaFileId: '', radius: 12, imageFit: 'cover' },
    video: { url: '', poster: '', mediaFileId: '', posterMediaFileId: '', radius: 12 },
    button: { content: 'Learn more', url: '/member-account', align: 'stretch', backgroundColor: '#2D8CFF', buttonTextColor: '#FFFFFF', radius: 8, buttonActionType: 'go_to', buttonGoToType: 'custom_link', buttonFeatureSlug: '', buttonOpenInNewTab: false },
  }
  const block = { id: ++blockSequence, type, ...presets[type] }
  draft.blocks.push(block)
  selectedBlockId.value = block.id
}

function blockTextStyle(block) {
  return { color: block.color, fontSize: `${Number(block.fontSize || 14)}px`, textAlign: block.align || 'left' }
}

function broadcastImageSrc(block) {
  const mediaFileId = String(block?.mediaFileId || '').trim()
  if (mediaFileId) return `${apiProxyUrl}/api/image/${encodeURIComponent(mediaFileId)}`
  const legacyUrl = String(block?.url || '').trim()
  return legacyUrl.startsWith('/') || /^https?:\/\//i.test(legacyUrl) ? legacyUrl : ''
}

function broadcastVideoFileId(block) {
  return String(block?.mediaFileId || '').trim()
}

function broadcastVideoPlaylistSrc(block) {
  const mediaFileId = broadcastVideoFileId(block)
  if (mediaFileId) return `${apiProxyUrl}/api/stream-hls/playlist/${encodeURIComponent(mediaFileId)}`
  const legacyUrl = String(block?.url || '').trim()
  return legacyUrl.startsWith('/') || /^https?:\/\//i.test(legacyUrl) ? legacyUrl : ''
}

function broadcastVideoPosterSrc(block) {
  const posterMediaFileId = String(block?.posterMediaFileId || '').trim()
  if (posterMediaFileId) {
    const revision = Number(block?.thumbnailRevision || 0)
    return `${apiProxyUrl}/api/image/${encodeURIComponent(posterMediaFileId)}${revision ? `?v=${revision}` : ''}`
  }
  const mediaFileId = broadcastVideoFileId(block)
  if (mediaFileId) {
    const revision = Number(block?.thumbnailRevision || 0)
    return `${apiProxyUrl}/api/video-thumbnail/${encodeURIComponent(mediaFileId)}${revision ? `?v=${revision}` : ''}`
  }
  const legacyPoster = String(block?.poster || '').trim()
  return legacyPoster.startsWith('/') || /^https?:\/\//i.test(legacyPoster) ? legacyPoster : ''
}

function buttonBlockStyle(block) {
  const align = block.align || 'left'
  return {
    backgroundColor: block.backgroundColor || draft.design.accentColor,
    color: block.buttonTextColor || '#FFFFFF',
    borderRadius: `${Number(block.radius || 0)}px`,
    fontSize: `${Number(block.fontSize || 14)}px`,
    marginLeft: align === 'center' || align === 'right' ? 'auto' : '0',
    marginRight: align === 'center' ? 'auto' : '0',
    width: align === 'stretch' ? '100%' : 'auto',
    justifyContent: 'center',
  }
}

function openMediaPicker(type) { activeMediaPicker.value = type }
function clearThumbnailRefreshTimer(blockId) {
  const timer = thumbnailRefreshTimers.get(blockId)
  if (timer) clearTimeout(timer)
  thumbnailRefreshTimers.delete(blockId)
}
function clearThumbnailRefreshTimers() {
  thumbnailRefreshTimers.forEach((timer) => clearTimeout(timer))
  thumbnailRefreshTimers.clear()
}
async function refreshGeneratedVideoThumbnail(block, attempt = 0) {
  const fileId = String(block?.mediaFileId || '').trim()
  if (!fileId || block?.posterMediaFileId || !props.open) return
  clearThumbnailRefreshTimer(block.id)
  block.thumbnailPending = true
  try {
    const response = await fetch(`${apiProxyUrl}/api/video-thumbnail/${encodeURIComponent(fileId)}?ready=${Date.now()}`, {
      credentials: 'include',
      cache: 'no-store',
    })
    const contentType = String(response.headers.get('content-type') || '').toLowerCase()
    if (response.ok && contentType.startsWith('image/')) {
      await response.blob()
      block.thumbnailRevision = Date.now()
      block.thumbnailPending = false
      return
    }
  } catch (_error) {
    // Video processing is asynchronous; retry below without surfacing a false editor error.
  }
  if (attempt >= 22 || !props.open) {
    block.thumbnailPending = false
    return
  }
  const timer = setTimeout(() => refreshGeneratedVideoThumbnail(block, attempt + 1), 4000)
  thumbnailRefreshTimers.set(block.id, timer)
}
function applySelectedMedia(media) {
  if (activeMediaPicker.value === 'avatar') {
    draft.avatarMediaFileId = String(media?.fileId || '')
    activeMediaPicker.value = ''
    return
  }
  if (!selectedBlock.value) return
  if (activeMediaPicker.value === 'video') {
    selectedBlock.value.mediaFileId = String(media?.fileId || '')
    selectedBlock.value.url = String(media?.hlsPlaylistUrl || media?.fileUrl || '')
    selectedBlock.value.poster = ''
    selectedBlock.value.posterMediaFileId = ''
    selectedBlock.value.thumbnailRevision = Date.now()
    selectedBlock.value.thumbnailPending = true
    refreshGeneratedVideoThumbnail(selectedBlock.value)
  } else if (activeMediaPicker.value === 'video-poster') {
    clearThumbnailRefreshTimer(selectedBlock.value.id)
    selectedBlock.value.posterMediaFileId = String(media?.fileId || '')
    selectedBlock.value.poster = ''
    selectedBlock.value.thumbnailRevision = Date.now()
    selectedBlock.value.thumbnailPending = false
  } else {
    selectedBlock.value.mediaFileId = String(media?.fileId || '')
    selectedBlock.value.url = ''
    if (!selectedBlock.value.alt) selectedBlock.value.alt = String(media?.fileName || 'Broadcast image')
  }
  activeMediaPicker.value = ''
}

function clearVideoPoster() {
  if (!selectedBlock.value || selectedBlock.value.type !== 'video') return
  selectedBlock.value.posterMediaFileId = ''
  selectedBlock.value.poster = ''
  selectedBlock.value.thumbnailRevision = Date.now()
  refreshGeneratedVideoThumbnail(selectedBlock.value)
}

function localParts(value, timeZone = 'America/New_York') {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(date).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]))
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}` }
}
function removeBlock(id) {
  const index = draft.blocks.findIndex((block) => block.id === id)
  if (index < 0) return
  clearThumbnailRefreshTimer(id)
  draft.blocks.splice(index, 1)
  selectedBlockId.value = draft.blocks[Math.min(index, draft.blocks.length - 1)]?.id || null
}

onBeforeUnmount(() => {
  if (memberSearchTimer) clearTimeout(memberSearchTimer)
  if (autosaveTimer) clearTimeout(autosaveTimer)
  clearThumbnailRefreshTimers()
})
function moveBlock(direction) {
  const index = selectedIndex.value
  const next = index + direction
  if (index < 0 || next < 0 || next >= draft.blocks.length) return
  const [block] = draft.blocks.splice(index, 1)
  draft.blocks.splice(next, 0, block)
}
function zonedLocalToIso(dateText, timeText, timeZone) {
  const [year, month, day] = String(dateText).split('-').map(Number)
  const [hour, minute] = String(timeText).split(':').map(Number)
  if (![year, month, day, hour, minute].every(Number.isFinite)) return ''
  const target = Date.UTC(year, month - 1, day, hour, minute, 0)
  let guess = target
  const formatter = new Intl.DateTimeFormat('en-US', { timeZone, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
  for (let pass = 0; pass < 2; pass += 1) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(guess)).filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]))
    const rendered = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour === 24 ? 0 : parts.hour, parts.minute, parts.second)
    guess += target - rendered
  }
  return new Date(guess).toISOString()
}

function buildPayload(deliveryAction, autosave = false) {
  const scheduledAt = deliveryAction === 'schedule' ? zonedLocalToIso(draft.date, draft.time, draft.timezone) : null
  const deleteScheduledAt = deliveryAction !== 'draft' && draft.deleteAfterDelivery
    ? zonedLocalToIso(draft.deleteDate, draft.deleteTime, draft.timezone)
    : null
  const firstText = draft.channel === 'sms' ? draft.smsMessage : draft.blocks.find((block) => ['heading', 'text'].includes(block.type))?.content
  return {
    ...draft,
    audiencePlanTierId: draft.audiencePlanTierId ? Number(draft.audiencePlanTierId) : null,
    audienceMemberIds: draft.audienceMemberIds.map(Number).filter(Boolean),
    blocks: draft.blocks.map((block) => ({ ...block })),
    design: { ...draft.design },
    body: firstText || 'Rich member broadcast',
    smsMessage: draft.smsMessage,
    scheduledAt,
    deleteAfterDelivery: deliveryAction !== 'draft' && draft.deleteAfterDelivery,
    deleteScheduledAt,
    deliveryAction,
    autosave,
  }
}

function submit(deliveryAction) {
  validationError.value = ''
  if (draft.channel === 'in-app' && !toastCopyIsCustomized.value) validationError.value = 'Customize both the toast headline and message before saving.'
  else if (draft.channel === 'in-app' && deliveryAction !== 'draft' && !toastAvatarIsSelected.value) validationError.value = 'Choose an engaging-as avatar before sending or scheduling.'
  else if (!draft.name.trim()) validationError.value = 'Add an internal broadcast name.'
  else if (draft.audience === 'tier' && !draft.audiencePlanTierId) validationError.value = 'Choose a plan tier.'
  else if (draft.audience === 'selected' && !draft.audienceMemberIds.length) validationError.value = 'Choose at least one member.'
  else if (deliveryAction === 'schedule' && (!draft.date || !draft.time)) validationError.value = 'Choose a delivery date and time.'
  else if (deliveryAction !== 'draft' && draft.deleteAfterDelivery && (!draft.deleteDate || !draft.deleteTime)) validationError.value = 'Choose when the member notification should be removed.'
  else if (draft.channel === 'sms' && !draft.smsMessage.trim()) validationError.value = 'Write the SMS message.'
  else if (draft.channel === 'sms' && deliveryAction !== 'draft' && !selectedSmsProvider.value?.connected) validationError.value = 'Connect or choose an available SMS provider before sending or scheduling.'
  if (validationError.value) return

  const scheduledAt = deliveryAction === 'schedule' ? zonedLocalToIso(draft.date, draft.time, draft.timezone) : null
  if (deliveryAction === 'schedule' && (!scheduledAt || new Date(scheduledAt).getTime() <= Date.now())) {
    validationError.value = 'Choose a delivery time in the future.'
    return
  }
  const deleteScheduledAt = deliveryAction !== 'draft' && draft.deleteAfterDelivery
    ? zonedLocalToIso(draft.deleteDate, draft.deleteTime, draft.timezone)
    : null
  const deliveryTimestamp = deliveryAction === 'schedule' ? new Date(scheduledAt).getTime() : Date.now()
  if (draft.deleteAfterDelivery && deliveryAction !== 'draft' && (!deleteScheduledAt || new Date(deleteScheduledAt).getTime() <= deliveryTimestamp)) {
    validationError.value = 'Schedule notification removal after the delivery time.'
    return
  }
  if (autosaveTimer) clearTimeout(autosaveTimer)
  const payload = buildPayload(deliveryAction, false)
  lastAutosaveSignature = JSON.stringify(buildPayload('draft', true))
  emit('save', payload)
}
</script>

<style scoped>
.editor-canvas { background-color: #f8fafc; background-image: radial-gradient(#cbd5e1 0.8px, transparent 0.8px); background-size: 18px 18px; scrollbar-width: thin; scrollbar-color: transparent transparent; scrollbar-gutter: stable; }
.editor-canvas::-webkit-scrollbar { width: 6px; height: 6px; }
.editor-canvas::-webkit-scrollbar-button { display: none; width: 0; height: 0; background: transparent; }
.editor-canvas::-webkit-scrollbar-track { background: transparent; }
.editor-canvas::-webkit-scrollbar-thumb { border-radius: 999px; background-color: transparent; }
.editor-canvas:hover { scrollbar-color: rgba(145, 150, 156, .54) transparent; }
.editor-canvas:hover::-webkit-scrollbar-thumb { background-color: rgba(140, 143, 148, .48); }
.editor-canvas:hover::-webkit-scrollbar-thumb:hover { background-color: rgba(100, 116, 139, .68); }
.editor-icon-button { display: inline-flex; height: 2.5rem; width: 2.5rem; align-items: center; justify-content: center; border-radius: .75rem; color: rgb(71 85 105); transition: background-color 150ms ease; }
.editor-icon-button:hover { background: rgb(241 245 249); }
.editor-name-input { width: 100%; border: 0; border-bottom: 1px solid transparent; padding: .15rem 0; color: rgb(15 23 42); font-size: 1rem; font-weight: 700; outline: none; }
.editor-name-input:focus { border-bottom-color: rgb(59 130 246); box-shadow: none; }
.editor-label { display: block; margin-bottom: .45rem; color: rgb(51 65 85); font-size: .75rem; font-weight: 700; }
.editor-control { display: block; width: 100%; min-height: 2.75rem; border: 1px solid rgb(203 213 225); border-radius: .75rem; background: white; padding: .68rem .8rem; color: rgb(15 23 42); font-size: .8125rem; line-height: 1.25rem; outline: none; transition: border-color 150ms ease, box-shadow 150ms ease; }
.editor-control::placeholder { color: rgb(148 163 184); }
.editor-control:focus { border-color: rgb(59 130 246); box-shadow: 0 0 0 3px rgb(59 130 246 / .12); }
.editor-control--icon { padding-left: 3.15rem !important; }
.editor-input-icon { pointer-events: none; position: absolute; inset: 0 auto 0 0; display: flex; width: 2.8rem; align-items: center; justify-content: center; color: rgb(148 163 184); }
.editor-move-button { display: inline-flex; align-items: center; justify-content: center; gap: .35rem; border: 1px solid rgb(226 232 240); border-radius: .65rem; padding: .55rem; color: rgb(71 85 105); font-size: .72rem; font-weight: 700; }
.editor-move-button:hover:not(:disabled) { background: rgb(248 250 252); color: rgb(37 99 235); }
.editor-move-button:disabled { cursor: not-allowed; opacity: .35; }
.editor-settings { scrollbar-width: thin; scrollbar-color: transparent transparent; scrollbar-gutter: stable; }
.editor-settings::-webkit-scrollbar { width: 6px; height: 6px; }
.editor-settings::-webkit-scrollbar-button { display: none; width: 0; height: 0; background: transparent; }
.editor-settings::-webkit-scrollbar-track { background: transparent; }
.editor-settings::-webkit-scrollbar-thumb { border-radius: 999px; background-color: transparent; }
.editor-settings:hover { scrollbar-color: rgba(145, 150, 156, .54) transparent; }
.editor-settings:hover::-webkit-scrollbar-thumb { background-color: rgba(140, 143, 148, .48); }
.editor-settings:hover::-webkit-scrollbar-thumb:hover { background-color: rgba(203, 213, 225, .65); }
.editor-color { display: block; width: 100%; height: 2.75rem; cursor: pointer; border: 1px solid rgb(203 213 225); border-radius: .75rem; background: white; padding: .3rem; }
.editor-color:focus-visible { border-color: rgb(59 130 246); outline: 3px solid rgb(59 130 246 / .12); }
</style>
